// `sort` pipeline verb — orders rows of the threaded dataset by one or
// more sort keys. Each positional arg is either a bare column-symbol
// (ascending) or a `(desc <col-symbol>)` opcall (descending). Same
// LLM-facing surface as `select` / `distinct` — bare symbols stand
// for columns of the threaded dataset.
//
// Special-form because the arg list mixes two AST shapes (symbolRef +
// desc opcall) and we want to inspect them shape-first rather than
// eager-evaluating. Eager evaluation of a bare symbol like `revenue`
// would route through `Env` / `Session.getBinding` — both of which
// might or might not have a binding for that name; neither would
// give us "column on the threaded dataset" semantics.

import { z } from 'zod';

import type { SourcePos } from '../../../ast.ts';
import { TypeMismatchError, UnsortableColumnTypeError } from '../../../errors.ts';
import { opCallPos } from '../../../normalize.ts';
import type { ColumnRefInArg, SpecialVerbCtx } from '../../../op-meta.ts';
import { pipelineVerbs } from '../../../op-meta.ts';
import { paramPath } from '../../../path.ts';
import { compileSortKeys, type SortKey, withTable } from '../../../runtime/arquero.ts';
import { passThroughSchema } from '../../../schema/infer.ts';
import { describeColumnType, isTemporalColumn } from '../../../schema/types.ts';
import { type Cell, describeValueKind, type MaterializedDataset } from '../../../value.ts';
import { parseSortEntry, SORT_KEYS_DOC } from '../../sort-entry.ts';

/** Column types that arquero's `orderby` can compare without help. */
const SORTABLE_KINDS = new Set(['number', 'string', 'boolean', 'date', 'timestamp', 'nullable']);
/**
 * Whether a runtime cell can be put in a natural order by arquero's
 * default comparator. `null`/`undefined` are tolerated (skipped,
 * SQL-NULL style — `orderby` already sinks them consistently). A
 * `Dataset` or struct-object cell has no natural order.
 */
const isNaturallyOrderable = (cell: Cell): boolean => {
  if (cell === null || cell === undefined) {
    return true;
  }
  const t = typeof cell;
  return t === 'number' || t === 'string' || t === 'boolean' || cell instanceof Date;
};

/**
 * Extract a `SortKey` from a single sort positional arg.
 *
 *   - `{$sym: 'name'}`        → `{column: 'name', direction: 'asc'}`
 *   - `(desc {$sym: 'name'})` → `{column: 'name', direction: 'desc'}`
 *   - anything else           → TypeMismatchError
 *
 * Shape discrimination is delegated to the shared `parseSortEntry`
 * kernel; this wrapper owns only the arquero `direction` mapping and
 * the `sort` verb's `TypeMismatchError` policy.
 */
const sortKeyFromArg = (arg: unknown, pos: SourcePos): SortKey => {
  const r = parseSortEntry(arg);
  if (r.ok) {
    return { column: r.column, direction: r.descending ? 'desc' : 'asc' };
  }
  if (r.reason === 'bad-desc-inner') {
    throw new TypeMismatchError(
      `a ${r.got}`,
      `a column symbol inside \`(desc …)\` — sort keys are ${SORT_KEYS_DOC}`,
      'sort',
      pos,
    );
  }
  throw new TypeMismatchError(`a ${r.got}`, SORT_KEYS_DOC, 'sort', pos);
};

/**
 * Verify a sort key's column can be ordered.
 *
 * Known-orderable schema kinds pass immediately. Genuinely
 * unorderable kinds (`struct`, `nested-dataset`, `array`, …) are
 * denied up front — we positively know they have no natural order, so
 * there is nothing to try. A column typed `unknown` is the open case:
 * every `transform`/`aggregate`/`join` output column is `unknown`
 * because derived types aren't inferred, so `unknown` means "not
 * inferred", NOT "known unsortable". We probe the actual runtime cells
 * and allow the sort when they are naturally orderable, failing only
 * on genuinely non-orderable data (a `Dataset`/struct cell) and then
 * with the runtime-detected kind. "Try first, deny on real failure."
 */
const checkSortable = (key: SortKey, dataset: MaterializedDataset, pos: SourcePos): void => {
  const colType = dataset.schema.get(key.column);
  if (colType === undefined) {
    // Schema-level "missing column" is normally a typecheck-pass error
    // (`UnknownColumnRefError`). The dispatch path can still see a
    // mismatch when the schema diverges from runtime; surface as a
    // type-mismatch for clarity.
    throw new TypeMismatchError(
      `column \`${key.column}\` to exist on the threaded dataset`,
      'no such column',
      'sort',
      pos,
    );
  }
  if (SORTABLE_KINDS.has(colType.kind)) {
    return;
  }
  if (colType.kind !== 'unknown') {
    // Positively known to have no natural order — deny without probing.
    throw new UnsortableColumnTypeError(key.column, describeColumnType(colType), pos);
  }
  // `unknown` = type not inferred. Probe the runtime values; allow the
  // sort unless the column genuinely holds unorderable cells.
  const offending = (dataset.columns[key.column] ?? []).find((cell) => !isNaturallyOrderable(cell));
  if (offending !== undefined) {
    throw new UnsortableColumnTypeError(key.column, describeValueKind(offending), pos);
  }
};

export const sort = z
  .object({
    name: z.literal('sort'),
    params: z.array(z.unknown()).min(1),
  })
  .describe(`Order rows by one or more keys. Each key is ${SORT_KEYS_DOC}.`)
  .register(pipelineVerbs, {
    evaluation: 'special',
    effect: 'pure',
    example: { expr: '(transform ds (sort name (desc capacity)))' },
    apply: (data, dataset: MaterializedDataset, ctx: SpecialVerbCtx): MaterializedDataset => {
      const pos = opCallPos(ctx.spans, ctx.path);
      const keys = (data.params as readonly unknown[]).map((arg) => {
        const key = sortKeyFromArg(arg, pos);
        checkSortable(key, dataset, pos);
        // Temporal columns sort by parsed instant, not arquero's
        // lexical default — `compileSortKeys` builds the date-aware
        // comparator when this flag is set.
        const colType = dataset.schema.get(key.column);
        return colType !== undefined && isTemporalColumn(colType) ? { ...key, dateAware: true } : key;
      });
      // arquero's `orderby` accepts column-name strings for asc and
      // `aq.desc(name)` wrappers for desc. We build the args list
      // dynamically. Using strings (not row functions) lets arquero
      // pick the most efficient comparator path.
      // `compileSortKeys` lives in `runtime/arquero.ts` — keeps the
      // §8.1 boundary intact (arquero's `desc(name)` is the engine
      // adapter for descending direction; we never import arquero
      // here).
      return withTable(dataset, (t) => t.orderby(...compileSortKeys(keys)));
    },
    propagateSchema: passThroughSchema,
    // Sort touches column values to determine ordering — declare the
    // ref so the typecheck pass diagnoses unknown columns AND the
    // dispatch wrapper's per-cell branch lifts on nested-dataset
    // columns (which won't sort, but the per-cell branch will reject
    // via the `checkSortable` guard inside each cell's apply). Derived
    // from the same `parseSortEntry` kernel the runtime path uses, so
    // the ref enumeration cannot drift from the shape rule.
    referencedColumns: (data, basePath): readonly ColumnRefInArg[] =>
      (data.params as readonly unknown[]).flatMap((arg, i) => {
        const r = parseSortEntry(arg);
        if (!r.ok) {
          return [];
        }
        // Path mirrors the AST: a bare symbol sits at `params/i`; a
        // `(desc <sym>)` wrapper puts the symbol at `params/i/params/0`.
        const path = r.descending ? paramPath(paramPath(basePath, i), 0) : paramPath(basePath, i);
        return [{ name: r.column, path }];
      }),
  });
